class DoctorAttendanceStrategy {
     constructor () {
          this.employeeType = 'Doctor';
     }

     calculateAttendanceStatus(day, roster, holidays, employeeData) {
          const dateObj = new Date(day.date);
          const punchIn = day.punch_in ? new Date(day.punch_in) : null;
          const punchOut = day.punch_out ? new Date(day.punch_out) : null;

          // Check if future date
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const currentDate = new Date(day.date);
          currentDate.setHours(0, 0, 0, 0);
          const isFutureDate = currentDate > today;

          // Priority 1: Future Date
          if (isFutureDate) {
               return {
                    status: "-",
                    lateMark: "-",
                    workingHours: "-",
                    statusColor: "#94a3b8",
                    remarks: "Future Date"
               };
          }

          // Priority 2: Weekly Off - Fixed Sunday
          const isSunday = dateObj.getDay() === 0;
          if (isSunday || roster?.custom_weekoff_date === day.date) {
               return {
                    status: "Week Off",
                    lateMark: "-",
                    workingHours: "-",
                    statusColor: "#3b82f6",
                    remarks: "Sunday - Weekly Off"
               };
          }

          // Priority 3: National Holiday (Only 26 Jan, 15 Aug, 2 Oct)
          const holidayObj = holidays?.find(h => h.holiday_date === day.date);
          if (holidayObj && this.isNationalHoliday(holidayObj.holiday_date)) {
               return {
                    status: holidayObj.holiday_name,
                    lateMark: "-",
                    workingHours: "-",
                    statusColor: "#8b5cf6",
                    remarks: "National Holiday"
               };
          }

          // Priority 4: No Roster Assigned
          if (!roster || !roster.shift_id) {
               return {
                    status: "LOP",
                    lateMark: "-",
                    workingHours: "-",
                    statusColor: "#ef4444",
                    remarks: "No shift assigned"
               };
          }

          // Priority 5: No Punch In/Out - Absent
          if (!punchIn || !punchOut) {
               return {
                    status: "Absent",
                    lateMark: "-",
                    workingHours: "-",
                    statusColor: "#ef4444",
                    remarks: "Missed punch"
               };
          }

          // Calculate working hours with grace period logic
          const workingHours = this.calculateWorkingHoursWithGrace(punchIn, punchOut, roster);

          if (punchIn && punchOut && roster) {
               const workedSeconds = Math.floor((punchOut - punchIn) / 1000);
               const shiftSeconds = (parseInt(roster.total_minutes) || 0) * 60;
               const shortfallSeconds = Math.max(0, shiftSeconds - workedSeconds);

               return {
                    status: "Present",
                    lateMark: "-",
                    workingHours,
                    statusColor: shortfallSeconds > 0 ? "#ffc107" : "#10b981", // Yellow if shortfall, green if complete
                    remarks: shortfallSeconds > 0 ? `Shortfall: ${this.secondsToHMS(shortfallSeconds)}` : "Complete",
                    shortfallSeconds,
                    hasShortfall: shortfallSeconds > 0
               };
          }

          // Rule: Any punch in/out means Present
          return {
               status: "Present",
               lateMark: "-",
               workingHours,
               statusColor: "#10b981",
               remarks: "Doctor attended duty"
          };
     }

     calculateWorkingHoursWithGrace(punchIn, punchOut, roster) {
          if (!punchIn || !punchOut || !roster) return '00:00:00';

          const shiftName = roster.shift_name || '';
          const isOpenShift = shiftName.startsWith('OP');

          if (isOpenShift) {
               // Open shift: Count all worked hours up to total_hours
               return this.calculateOpenShiftHours(punchIn, punchOut, roster);
          } else {
               // Regular shift: Apply grace period logic
               return this.calculateRegularShiftHours(punchIn, punchOut, roster);
          }
     }

     calculateOpenShiftHours(punchIn, punchOut, roster) {
          // For open shifts, doctor can work any time during the day
          // Count total worked hours but max up to shift's total_hours
          const workingSeconds = Math.floor((punchOut - punchIn) / 1000);
          const maxSeconds = (parseInt(roster.total_minutes) || 480) * 60; // Convert minutes to seconds

          const finalSeconds = Math.min(workingSeconds, maxSeconds);
          return this.secondsToHMS(finalSeconds);
     }

     calculateRegularShiftHours(punchIn, punchOut, roster) {
          // Validate that roster has required shift timings
          if (!roster.in_time || !roster.out_time) {
               // If shift timings are not available, just calculate actual worked hours
               const workingSeconds = Math.floor((punchOut - punchIn) / 1000);
               return this.secondsToHMS(workingSeconds);
          }

          // Parse shift timings from roster data only
          const [shiftInHour, shiftInMin] = roster.in_time.split(':').map(Number);
          const [shiftOutHour, shiftOutMin] = roster.out_time.split(':').map(Number);

          // Create shift timing objects for the same date as punch
          const shiftDate = new Date(punchIn);
          const shiftInTime = new Date(shiftDate);
          shiftInTime.setHours(shiftInHour, shiftInMin, 0, 0);

          const shiftOutTime = new Date(shiftDate);
          shiftOutTime.setHours(shiftOutHour, shiftOutMin, 0, 0);

          // Handle next day shifts
          if (shiftOutTime <= shiftInTime) {
               shiftOutTime.setDate(shiftOutTime.getDate() + 1);
          }

          // Apply grace periods
          const graceInMinutes = parseInt(roster.grace_in) || 0;
          const graceOutMinutes = parseInt(roster.grace_out) || 0;

          // Calculate effective working period
          const graceOutTime = new Date(shiftOutTime);
          graceOutTime.setMinutes(graceOutTime.getMinutes() + graceOutMinutes);

          // Determine effective punch out time (don't count beyond grace_out)
          const effectivePunchOut = punchOut > graceOutTime ? graceOutTime : punchOut;

          // Calculate working seconds
          const workingSeconds = Math.floor((effectivePunchOut - punchIn) / 1000);

          // Ensure minimum 0 and reasonable maximum
          const maxSeconds = (parseInt(roster.total_minutes) || 480) * 60;
          const finalSeconds = Math.max(0, Math.min(workingSeconds, maxSeconds));

          return this.secondsToHMS(finalSeconds);
     }

     calculateWorkingHours(punchIn, punchOut) {
          // Legacy method - kept for backward compatibility
          if (!punchIn || !punchOut) return '00:00:00';

          const punchInDate = new Date(punchIn);
          const punchOutDate = new Date(punchOut);
          const diffMs = punchOutDate - punchInDate;

          if (diffMs <= 0) return '00:00:00';

          const totalSeconds = Math.floor(diffMs / 1000);
          return this.secondsToHMS(totalSeconds);
     }

     // Helper method to convert seconds to HH:MM:SS format
     secondsToHMS(totalSeconds) {
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
     }

     isNationalHoliday(date) {
          // Only three national holidays: Republic Day, Independence Day, Gandhi Jayanti
          const nationalHolidays = ['01-26', '08-15', '10-02'];
          const dateStr = date.substring(5); // Extract MM-DD from YYYY-MM-DD
          return nationalHolidays.includes(dateStr);
     }

     // No TDS calculation needed for attendance
     calculateTDS(grossEarnings, panLinked, panOperative) {
          return {
               tds_amount: (grossEarnings * 10) / 100,
               tds_rate: 10,
               pan_status: panOperative ? "Operative" : "Inoperative",
               pan_aadhaar_linked: panLinked
          };
     }
}

export default DoctorAttendanceStrategy;